// 介錯「据えの突き」(exec_maulSetPierce) の v3 (2026-10-09 夜に比較版 → 同日ユーザー採用「v3（溜め600）で本番へ」= 配布版の既定)。
//
//   据えの一打 (exec_maulSet) の溜めの間に4発当てると突きへ切り替わる (08-31 ユーザー裁定「Aで」)。
//   旧の版は切り替わっても絵が据えの構え (sweep_coil) のままで、突きの溜めは 350ms。
//   4発目を振り終えてから当たるまで 60〜140ms しか残らず、何が来るか見えても避けられない計算になっていた。
//   案と推奨 = C:\dev\LUNAIRE-notes\assets\2026-10-09-night-review\analysis\exec-brace-thrust-tell.md (案3)。
//
//   v3 (既定) で変わる3つ。4発で切り替える条件・突きの後の隙 1500ms・ダメージは変えない。
//     1. 切り替わった瞬間に構えを突き当ての溜め (jab_coil・切っ先を相手へ向けた構え) へ1コマで替える。
//        突き出しも突き当てと同じ jab_lunge。赤い突き2つの見え方がそろう。
//     2. 突きの溜めを 350ms → 600ms (attacks.ts の chargeMs。DEV の &braceThrustCharge=500|700 で比べられる)。
//     3. 切り替わった瞬間に危険音 (danger) を1回鳴らす (game.ts。旧の版でも鳴らす = 10-09 ユーザー「今の版でも切り替わりで鳴らす」)。
//
//   旧へ戻す = DEV の ?braceThrust=old (溜め 350・据えの構えのまま → sweep_slash)。
//   ?pierceArt= と ?pierceCharge= (09-04 の絵と溜めの単独つまみ) は ?braceThrust=old と一緒に使う
//   (v3 は切り替わった tick に絵を組み直すので ?pierceArt は効かない)。
//   本番化の記録 = docs/research/2026-10-09-prod-switch-brace-finisher-counter.md
import { getAttack } from './attacks'
import { PLAYER_DISPLAY_HEIGHT } from './balance'
import {
  BOSS_ATTACK_TIMELINES, BRACE_THRUST_DEFAULT_START, EXEC_PIERCE_TIMELINE_VARIANTS, braceThrustV3Timeline,
  phaseProgressOf, resolveTimelineMotion, type BraceThrustStart,
} from './boss_pose_timeline'
import { resolveBossSpriteScaleParams } from './sprites'
import type { Boss, SfxId, SpriteKey } from './types'

export { braceThrustV3Timeline, type BraceThrustStart }

export type BraceThrustMode = 'old' | 'v3'
export const BRACE_THRUST_CHARGE_CHOICES = [500, 600, 700] as const
// 既定 600 = attacks.ts の exec_maulSetPierce.chargeMs と同じ値 (tests/exec_brace_thrust.test.ts が照合)。
export const BRACE_THRUST_DEFAULT_CHARGE_MS = 600
// 旧の版の溜め (2026-08-30 実プレイ A-2『もうワンテンポ早く』で 450→350)。?braceThrust=old で戻す値。
export const BRACE_THRUST_OLD_CHARGE_MS = 350

const BOSS_ID = 'the-executioner'
const FROM_ATTACK_ID = 'exec_maulSet'
const TO_ATTACK_ID = 'exec_maulSetPierce'

// 既定 = v3 (10-09 ユーザー採用・配布版)。初期値は attacks.ts (chargeMs 600) と boss_pose_timeline.ts (v3 の絵) が持つ。
let mode: BraceThrustMode = 'v3'

export function braceThrustMode(): BraceThrustMode { return mode }

export function parseBraceThrustCharge(raw: string | null): number {
  const n = Number(raw)
  return (BRACE_THRUST_CHARGE_CHOICES as readonly number[]).includes(n) ? n : BRACE_THRUST_DEFAULT_CHARGE_MS
}

// DEV の URL の値から切り替える (game.ts の DEV の箱が ?pierceCharge / ?pierceArt より先に呼ぶ)。
//   ?braceThrust=old (または now) = 旧の版。?braceThrust=v3 か &braceThrustCharge= があれば v3 をその溜めで。
//   どちらも無ければ何もしない (= 既定の v3・溜め 600 のまま)。
export function setBraceThrustFromParams(rawMode: string | null, rawCharge: string | null): void {
  if (rawMode === 'old' || rawMode === 'now') setBraceThrustMode('old')
  else if (rawMode === 'v3' || rawCharge !== null) setBraceThrustMode('v3', parseBraceThrustCharge(rawCharge))
}

export function setBraceThrustMode(next: BraceThrustMode, chargeMs = BRACE_THRUST_DEFAULT_CHARGE_MS): void {
  const atk = getAttack(TO_ATTACK_ID)
  const table = BOSS_ATTACK_TIMELINES[BOSS_ID]!
  if (next === 'v3') {
    atk.chargeMs = chargeMs
    table[TO_ATTACK_ID] = braceThrustV3Timeline(BRACE_THRUST_DEFAULT_START)
  } else {
    atk.chargeMs = BRACE_THRUST_OLD_CHARGE_MS
    table[TO_ATTACK_ID] = EXEC_PIERCE_TIMELINE_VARIANTS.old
  }
  mode = next
}

// ===== 切り替わりで足が跳ばない置き方 =====
//   据えの構え sweep_coil と突きの構え jab_coil は、絵の中の足の位置も横の置き場所の決め方も違う
//   (sweep_coil = 不透明 bbox の中心 / jab_coil = sprites.ts EXEC_JAB_LUNGE_ANCHOR_X の 1195)。
//   そのまま替えると両足が画面で約 10〜15px 前へ跳ぶ。両足の靴底の中点が同じ床の位置に来るように、
//   切り替わった瞬間の据えの構えの位置 (タイムラインの offset と横の縮み) から突きの構えの offset を決める。
//   足の位置 = 靴底から上 50px (原画) の帯の左右の端の中点。測り方 = 2026-10-09 PIL で alpha>16。
//     sweep_coil: 前足 285〜429 → 357 / 後ろ足 1008〜1175 → 1091.5 / bbox 285〜1398 (中心 841.5)
//     jab_coil  : 前足 537〜708 → 622.5 / 後ろ足 1318〜1433 → 1375.5
//   両足の幅は画面で 125px×縮み と 116px でほぼ同じなので、中点を合わせれば前後の足も 5px 以内で重なる。
const SWEEP_COIL_ANCHOR_X = (285 + 1398) / 2
const SWEEP_COIL_FEET_MID_X = (357 + 1091.5) / 2
const JAB_COIL_ANCHOR_X = 1195
const JAB_COIL_FEET_MID_X = (622.5 + 1375.5) / 2
// 介錯の表示の高さ = 主人公 120 × render.ts BOSS_HEIGHT_RATIO 1.6 (テストが bossDisplayHeightFor と照合)。
export const EXEC_DISPLAY_HEIGHT = PLAYER_DISPLAY_HEIGHT * 1.6

function screenPxPerSourcePx(key: SpriteKey): number {
  const p = resolveBossSpriteScaleParams(BOSS_ID, 'blade', key, false)
  return EXEC_DISPLAY_HEIGHT * p.poseScaleFix / (p.refH ?? 1)
}

// 足の中点が置き場所 (ボスの x) からどれだけ後ろにあるか (画面px・+ = 後ろ)。
function feetMidBehindPx(key: 'sweep_coil' | 'jab_coil', scaleX: number): number {
  const [feet, anchor] = key === 'sweep_coil'
    ? [SWEEP_COIL_FEET_MID_X, SWEEP_COIL_ANCHOR_X] : [JAB_COIL_FEET_MID_X, JAB_COIL_ANCHOR_X]
  return scaleX * screenPxPerSourcePx(key as SpriteKey) * (feet - anchor)
}

// 据えの構えの位置 (offset・横の縮み・傾き) から、足が動かない突きの構えの置き方を出す。
//   offset は前方向が正。絵の中の位置 = 足の後ろ寄り − offset なので、
//   feetSweep(縮み) − offsetSweep = feetJab − offsetJab を解く。傾きは足元が軸なので持ち越して戻す。
export function braceThrustStartFrom(sweep: { offsetX: number; scaleX: number; rot: number }): BraceThrustStart {
  const offsetX = sweep.offsetX + feetMidBehindPx('jab_coil', 1) - feetMidBehindPx('sweep_coil', sweep.scaleX)
  return { offsetX: Math.round(offsetX * 10) / 10, rot: sweep.rot }
}

// 切り替わる前の据えの一打のタイムラインで、いまの位置を引く。
export function sweepMotionAt(boss: Boss): { offsetX: number; scaleX: number; rot: number } {
  const tl = BOSS_ATTACK_TIMELINES[BOSS_ID]![FROM_ATTACK_ID]!
  const { phase, progress, hitIndex } = phaseProgressOf(boss)
  const m = resolveTimelineMotion(tl, phase, progress, hitIndex)
  return { offsetX: m.offsetX, scaleX: m.scaleX, rot: m.rot }
}

// 据えの一打 → 据えの突きへ切り替わった tick か (前の tick も今の tick も予兆)。版に関係なく判定だけする。
export function braceThrustSwitched(prev: Boss, next: Boss): boolean {
  return prev.state === 'telegraph' && next.state === 'telegraph'
    && prev.currentAttack?.id === FROM_ATTACK_ID
    && prev.currentAttack.greedSwitch?.toAttackId === TO_ATTACK_ID
    && next.currentAttack?.id === TO_ATTACK_ID
}

// 切り替わった tick に game.ts が呼ぶ (配布版も)。v3 の時だけ、足が跳ばない置き方で突きの絵を組み直し、
//   危険音を返す。旧の版では何もしない (危険音は game.ts が1回鳴らす)。
export function onBraceThrustSwitch(prev: Boss): SfxId[] {
  if (mode !== 'v3') return []
  BOSS_ATTACK_TIMELINES[BOSS_ID]![TO_ATTACK_ID] = braceThrustV3Timeline(braceThrustStartFrom(sweepMotionAt(prev)))
  return ['danger']
}
